/**
 * 「이거 분실물 접수 아닌가요?」 신호 — **고객문의 폼에서만** 쓰는 힌트.
 *
 * 🔴 이건 분류기가 아니라 **한 줄 제안**이다. 틀려도 고객은 그 줄을 무시하면 그만이고, 맞으면
 *    노선·분실일자·시간대를 함께 받는 창구로 넘어간다. 그래서 판정을 **재현율 쪽으로 기울인다** —
 *    자동으로 유형을 바꾸거나 접수를 막는 데는 **절대 쓰지 않는다**(그건 틀렸을 때 고객을 잃는다).
 *
 * 실측이 규칙을 정했다(`scripts/probe-lost-in-inquiry.cjs` · prod 3,506건 · 회귀 가드
 * `scripts/verify-lost-hint.cjs` 는 **전부 실문장**으로 판정한다):
 *   - 고객문의 1,636건 중 **52건(3.2%)** 이 실은 분실물이다 — 2026-09-10 한화시스템 `EVMH2H`
 *     (지갑)가 이 경로로 들어와 분실 일자·시간대를 통째로 못 받았고 영업담당이 되물어야 했다.
 *   - 🔴 **`두고|놓고` 단독은 오탐 기계다** — "정해놓고 운행", "문 열어놓고", "티켓은 구매하게
 *     해놓고", "불법주차해놓고", "나와놓고" 가 전부 걸렸다(실측 10건이 이 모양). 그래서
 *     **뒤에 «내/하차»가 붙을 때만** 신호로 친다.
 *   - 🔴 **`분실` 단독도 못 쓴다** — "사원증 분실로 재발급", "회원 아이디 분실", "NFC스티커
 *     분실로 재등록" 은 분실물 접수가 아니라 계정·카드 재발급 문의다(실측 8건).
 *
 * ⚠️ 물건 이름 목록(지갑·가방·핸드폰…)을 동반 조건으로 두려다 **뺐다** — prod 전수에서 그 조건이
 *    거르는 문장이 **0건**이었다(분실 + 비재발급인데 물건 이름이 없는 문의가 실재하지 않는다).
 *    한 건도 안 거르는 가지는 규칙을 지키는 게 아니라 규칙을 못 읽게 만든다.
 *
 * ⚠️ 한글 NFC 정규화 필수 — 브라우저 입력(NFD 자모조합)과 리터럴(NFC)이 다르면 `test` 가
 *    조용히 0을 돌려준다(`csBotCore` FAQ 매칭에서 이미 밟은 함정).
 */

/** 그 자체로 «버스에 두고 내렸다»를 뜻하는 말 — 단독 성립. */
const STRONG =
  /분실물|분실\s*신고|분실\s*접수|잃어버|놓고\s*내|두고\s*내|놓고\s*하차|두고\s*하차|흘리고\s*내|떨어뜨리고\s*내/;

/**
 * 계정·카드 **재발급/재등록** 문맥 — 여기서의 '분실'은 물건 접수가 아니다.
 * 🔴 STRONG 이 이미 잡은 건은 이 필터를 **지나지 않는다** — "분실물 확인 요청… 글 등록이
 *    안돼서 여기 씁니다" 같은 진짜 분실물 글이 «등록» 한 단어로 죽으면 안 된다.
 */
const REISSUE = /재발급|재등록|신규발급|아이디|비밀번호|계정|스티커|태그|rfid|nfc/;

/** 이 문의가 분실물 접수로 보이나. **힌트 전용** — 판정·차단·자동전환에 쓰지 말 것. */
export function looksLikeLostItem(text: string | null | undefined): boolean {
  const t = String(text ?? '')
    .normalize('NFC')
    .toLowerCase();
  // 아직 타이핑 중인 두세 글자에 제안을 띄우면 «따라다니는 경고»가 된다.
  if (t.length < 4) return false;
  if (STRONG.test(t)) return true;
  if (!t.includes('분실')) return false;
  return !REISSUE.test(t);
}
